import { List } from 'immutable';
import memoizeOne from 'memoize-one';
import React from 'react';
import { connect } from 'react-redux';
import { Product } from '~/Api/Product';
import { Filter, UserProductGroup, UserProductGroupErrorResponse } from '~/Api/User/ProductGroup';
import { Meta } from '~/Api/User/ProductGroup/Product';
import config from '~/config';
import FilterParameter from '~/utils/FilterParameter/FilterParameter';
import { fetchCurrentCustomerDatasourceLicencesIfNeeded } from '~/CustomerDatasourceLicence';
import { handleError } from '~/ErrorHandler';
import { flashSuccessMessage } from '~/FlashMessage';
import flashMessageStyles from '~/FlashMessage/styles.less';
import { logGoogleTagManagerShareLink } from '~/GoogleTagManager';
import { addProductsToGroup } from '~/GroupProduct';
import { PriceOrigin, ProductPrice } from '~/Product';
import { fetchGroups } from '~/ProductGroup';
import { LinkProvider } from '~/ProductGroupDetail';
import { fetchPrices, selectProductPrices } from '~/ProductPrice';
import { ApplicationState, ThunkDispatch } from '~/Store';
import { Link } from '~/UI/Button';

import DetailModal from './DetailModal';

function fillMissingPrices(prices: ProductPrice[] | null): List<ProductPrice> {
	const map: { [key: string]: ProductPrice } = {};
	if (prices) {
		prices.forEach((price) => (map[price.type] = price));
	}

	return List(
		config.product.priceTypesOrder.map((type) => {
			return map[type] !== undefined ? map[type] : new ProductPrice(type, null, 0, new PriceOrigin('source', null));
		})
	);
}

const memoizedFillMissingPrices = memoizeOne(fillMissingPrices);

interface OwnProps {
	currency: string | null;
	product: Product | null;
	productId: string;
	groupId: string | null;
	shareLink?: string;
}

function mapStateToProps(state: ApplicationState, props: OwnProps) {
	const { currency = config.product.defaultPriceCurrency, product } = props;
	const prices =
		currency === config.product.defaultPriceCurrency
			? product
				? product.prices
				: null
			: selectProductPrices(state.productPrice, props.productId, currency);

	return { prices: memoizedFillMissingPrices(prices) };
}

function mapDispatchToProps(dispatch: ThunkDispatch<any>, props: OwnProps) {
	return {
		onFetch: () => {
			dispatch(fetchCurrentCustomerDatasourceLicencesIfNeeded());

			if (props.currency !== config.product.defaultPriceCurrency) {
				dispatch(fetchPrices([props.productId], props.currency, props.groupId)).catch((response) =>
					dispatch(handleError(response))
				);
			}
		},
		onAddProductToGroup: (
			group: UserProductGroup,
			product: Product,
			filterParameters: FilterParameter<string | string[]>[] | null
		) => {
			return dispatch(addProductsToGroup(group, [product], new Meta(filterParameters || [])))
				.then((response) => {
					dispatch(
						flashSuccessMessage(
							<span>
								Drug added to list{' '}
								<LinkProvider group={group}>
									{(link) => (
										<Link styles={flashMessageStyles} title={group.name} to={link}>
											{group.name}
										</Link>
									)}
								</LinkProvider>
							</span>
						)
					);
					return response;
				})
				.catch((response) => {
					if (!(response instanceof UserProductGroupErrorResponse)) {
						dispatch(handleError(response));
						return;
					}
					if (response.handleProductGroupNotFound()) {
						dispatch(fetchGroups(new Filter(null, null, null)));
					}
					dispatch(handleError(response));
				});
		},
		onShare: () =>
			logGoogleTagManagerShareLink('product', props.shareLink !== undefined ? props.shareLink : window.location.href),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(DetailModal);
